<script lang="ts">
  import type { Area } from '../../shared/contract'
  import Groomed from './Groomed.svelte'
  import Lamp from './Lamp.svelte'
  import RunGroups from './RunGroups.svelte'
  import Sign from './Sign.svelte'
  import {
    GRADE_NAME,
    byGrade,
    count,
    dead,
    exceptions,
    filtering,
    liftRows,
    matches,
    runState,
    sortRuns,
    turning,
    type Filter,
  } from './tally'

  /**
   * One area on flat glass: its lifts named with lamps, then one tally line,
   * then the few exceptions named. Folds to a 44 px line when another area is
   * open, when nothing here matches the filter, or when nothing here runs.
   * Opened, it lists every run by grade; that list is the only thing that scrolls.
   */
  let {
    area,
    anyOpen,
    filter,
    desk = false,
    open = false,
    folded = false,
    selected = false,
    named = false,
    withLifts = true,
    holdRows = true,
    shown,
    excShown,
    brief = false,
    ontoggle,
  }: {
    area: Area
    anyOpen: boolean
    filter: Filter
    /** On the desktop the plates never change shape: the board does the work. */
    desk?: boolean
    open?: boolean
    /** Another area is open on a phone. */
    folded?: boolean
    /** The area the desktop board is focused on. */
    selected?: boolean
    /** Early season: the one area with anything open names its open runs. */
    named?: boolean
    /** Lift names stay only while everything fits; then the lifts fold to lamps. */
    withLifts?: boolean
    /** A held lift takes a whole row for its word, while there is room for that. */
    holdRows?: boolean
    /** How many named runs fit; the rest become "+N more". */
    shown?: number
    /** How many exceptions fit on their one line; the rest become "+N". */
    excShown?: number
    /** A crowded day: this plate's exceptions line gives way (its tally still counts them). */
    brief?: boolean
    ontoggle: (name: string) => void
  } = $props()

  const f = $derived(filtering(filter) && !desk)
  const isDead = $derived(dead(area, anyOpen))
  const hits = $derived(sortRuns(area.runs.filter((r) => matches(r, filter))))
  const mini = $derived(!open && (folded || (f && !hits.length) || isDead))
  const runs = $derived(count(area.runs))
  const groomed = $derived(area.runs.filter((r) => r.groomed).length)
  const rows = $derived(liftRows(area.lifts))
  const turningLifts = $derived(area.lifts.filter(turning).length)
  const grades = $derived(byGrade(area.runs))
  const exc = $derived(brief ? null : exceptions(area, anyOpen))
  const excVisible = $derived(exc && excShown != null ? exc.runs.slice(0, excShown) : exc?.runs)
  /** The runs named inline: matches under a filter, or the open runs early in the season. */
  const named$ = $derived(
    f && !open
      ? hits
      : named && !f && !open
        ? sortRuns(area.runs.filter((r) => r.open))
        : null,
  )
  const visible = $derived(named$ && shown != null ? named$.slice(0, shown) : named$)
  const lampsOnly = $derived(!withLifts && !open)
  const label = $derived(
    `${area.name}: ${
      f
        ? `${hits.length || 'no'} matching run${hits.length === 1 ? '' : 's'}`
        : anyOpen
          ? `${runs.open} of ${runs.total} runs open`
          : `${runs.total} runs`
    }, ` +
      (area.lifts.length
        ? `${turningLifts} of ${area.lifts.length} lift${area.lifts.length > 1 ? 's' : ''} turning`
        : 'no lifts'),
  )
  /*
   * Lifts in two columns, in the report's order. While there is room, a held
   * lift takes a whole row so its word never crowds a name; so does a lift
   * left alone in a row. Long names also use the full width.
   */
  const fullRow = (lift: { name: string; status?: string } | undefined) => !!lift && (lift.name.length > 20 || (holdRows && !!lift.status))
  const wide = $derived.by(() => {
    let col = 0
    return rows.map((lift, i) => {
      const next = rows[i + 1]
      if (fullRow(lift) || (col === 0 && (!next || fullRow(next)))) {
        col = 0
        return true
      }
      col = 1 - col
      return false
    })
  })
</script>

<div
  class="plate"
  class:mini
  class:open
  class:nolift={!area.lifts.length}
  class:dead={isDead}
  class:sel={selected}
  class:named={named && !f && !open}
  class:filtering={f}
  class:lamps-only={lampsOnly}
  class:desk
>
  <div class="top">
    <button
      class="ph"
      data-area={area.name}
      aria-expanded={desk ? undefined : open}
      aria-pressed={desk ? selected : undefined}
      aria-controls={desk ? 'board' : undefined}
      aria-label={label}
      onclick={() => ontoggle(area.name)}
    >
      <span class="lbl mono">{area.name}</span>
      {#if area.lifts.length && !(desk && isDead)}
        <span class="dots" aria-hidden="true">
          {#each area.lifts as lift}<Lamp on={turning(lift)} small />{/each}
        </span>
      {/if}
      <span class="rt">
        <span class="lc mono">
          {#if area.lifts.length}
            <b>{turningLifts}</b>/{area.lifts.length} lift{area.lifts.length > 1 ? 's' : ''}
          {:else}
            no lifts
          {/if}
        </span>
        <span class="rc">
          {#if f}
            {#if hits.length}<b>{hits.length}</b> match{:else}no match{/if}
          {:else if isDead}
            <span class="mono">Closed</span>{runs.total} runs
          {:else if anyOpen}
            <span><b>{runs.open}</b>/{runs.total} runs</span>
          {:else}
            {runs.total} runs
          {/if}
          {#if !desk}<i class="chev"></i>{/if}
        </span>
      </span>
    </button>
    {#if area.lifts.length}
      <ul class="lifts">
        {#each rows as lift, i}
          <li class="lift" class:on={lift.turning} class:wide={wide[i]} aria-label={lift.label} title={lift.label}>
            <Lamp on={lift.turning} />
            <span class="name">{lift.name}</span>
            {#if lift.status}<span class="hold mono">{lift.status}</span>{/if}
          </li>
        {/each}
      </ul>
    {/if}
  </div>

  {#if !open}
    <div class="tally">
      {#each grades as g (g.grade)}
        {#if anyOpen}
          <span class="t" class:z={!g.open}>
            <Sign grade={g.grade} off={!g.open} /><span aria-hidden="true"
              ><b>{g.open}</b>/{g.total}</span
            ><span class="vh">{GRADE_NAME[g.grade]}: {g.open} of {g.total} open</span>
          </span>
        {:else}
          <span class="t">
            <Sign grade={g.grade} off /><b aria-hidden="true">{g.total}</b><span class="vh"
              >{GRADE_NAME[g.grade]}: {g.total} runs</span
            >
          </span>
        {/if}
      {/each}
      {#if anyOpen && runs.open}
        <span class="t"
          ><Groomed /><b aria-hidden="true">{groomed}</b><span class="vh">{groomed} groomed</span
          ></span
        >
      {/if}
      <span class="go">
        {#if anyOpen}<span><b>{runs.open}</b>/{runs.total}</span>{:else}<span>{runs.total} runs</span>{/if}
        {#if !desk}<i class="chev"></i>{/if}
      </span>
    </div>
    {#if exc && excVisible}
      <div class="exc">
        <span class="mono">{exc.kind}</span>
        <span class="xs" data-exc={area.name}>
          {#each excVisible as run, i}
            {#if i}<span class="sep" aria-hidden="true">·</span>{/if}
            <span class="x" data-x
              ><Sign grade={run.grade} off={!run.open} />{run.name}<span class="vh"
                >, {runState(run)}</span
              ></span
            >
          {/each}
          {#if excVisible.length < exc.runs.length}
            {#if excVisible.length}<span class="sep" aria-hidden="true">·</span>{/if}
            <span class="x more">+{exc.runs.length - excVisible.length} <i class="chev"></i></span>
          {/if}
        </span>
      </div>
    {/if}
    {#if visible && !mini}
      <div class="hits" data-hits={area.name}>
        {#if !f}<span class="hit lab mono">Open</span>{/if}
        {#each visible as run}
          <span class="hit" class:off={!run.open} data-hit>
            <Sign grade={run.grade} off={!run.open} />{run.name}
            {#if run.groomed && filter.status !== 'groomed'}<Groomed />{/if}
            <span class="vh">, {runState(run)}</span>
          </span>
        {/each}
        {#if named$ && visible.length < named$.length}
          <span class="hit more">+{named$.length - visible.length} more <i class="chev"></i></span>
        {/if}
      </div>
    {/if}
  {:else}
    <!-- A scroller WebKit won't put in the tab order by itself. -->
    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <div class="runs" role="region" aria-label="{area.name} runs" tabindex="0">
      <RunGroups runs={area.runs} {filter} {anyOpen} />
    </div>
  {/if}
</div>

<style>
  .plate {
    position: relative;
    flex: none;
    background: var(--glass);
    border: 1px solid var(--glass-edge);
    border-radius: var(--r);
    padding: 9px 14px 0;
  }
  /* The header button's target is the whole plate (or, opened, the header and lifts). */
  .ph::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
  }
  .open .top {
    position: relative;
  }
  .ph {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 18px;
    width: 100%;
    text-align: left;
    border-radius: var(--r);
  }
  .ph:focus-visible {
    outline: none;
  }
  .ph:focus-visible::after {
    outline: 2px solid var(--amber-ink);
    outline-offset: 2px;
  }
  .lbl {
    flex: 1;
    min-width: 0;
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .rt {
    color: var(--ink-3);
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .rt b {
    color: var(--ink);
    font-weight: 400;
  }
  .rc {
    display: none;
  }
  .dots {
    display: none;
    gap: 5px;
    align-items: center;
    flex: none;
  }

  .lifts {
    display: grid;
    grid-auto-rows: var(--row);
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 12px;
    margin-top: 3px;
  }
  .lift.wide {
    grid-column: 1 / -1;
  }
  .lift {
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 0;
    color: var(--ink-3);
    font-size: 15px;
  }
  .lift .name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
  /* The name keeps its room; a word too long for what is left ends in an ellipsis. */
  .lift .hold {
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--ink-2);
    letter-spacing: 0.08em;
  }
  .lift.on {
    color: var(--ink);
  }

  .tally {
    display: flex;
    align-items: center;
    gap: 12px;
    height: calc(var(--row) + 12px);
    border-top: 1px solid var(--line);
    margin-top: 5px;
    font-size: 14px;
    color: var(--ink-3);
    white-space: nowrap;
  }
  .nolift .tally {
    border-top: 0;
    margin-top: 0;
  }
  .t {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .t b {
    font-weight: 400;
    color: var(--ink);
  }
  .t.z b {
    color: var(--ink-3);
  }
  .go {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 7px;
    color: var(--ink-3);
  }
  .go b {
    color: var(--ink);
    font-weight: 400;
  }
  .open .ph .chev {
    transform: rotate(-135deg);
    margin-top: 4px;
  }

  .exc {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-top: -6px;
    padding-bottom: 9px;
    font-size: 13px;
    line-height: 18px;
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
  }
  .exc .mono {
    color: var(--ink-3);
    flex: none;
  }
  .xs {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    overflow: hidden;
  }
  .x {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .x.more {
    font: 400 11px/18px var(--mono);
    letter-spacing: 0.1em;
    color: var(--ink-2);
  }
  .sep {
    color: var(--ink-3);
  }

  /* Folded: one 44 px line, lamps still lit. */
  .mini {
    padding-top: 0;
    padding-bottom: 0;
    height: 44px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .mini .lifts,
  .mini .tally,
  .mini .exc {
    display: none;
  }
  .mini .ph {
    height: 42px;
  }
  .mini .lc {
    display: none;
  }
  .mini .dots {
    display: flex;
  }
  .mini .rc,
  .filtering .rc {
    display: flex;
    align-items: center;
    gap: 7px;
    color: var(--ink-3);
    font-size: 13px;
    white-space: nowrap;
  }
  .rc :global(.mono) {
    color: var(--ink-3);
  }

  /* A filter on: each plate names its matching runs. */
  .filtering .tally,
  .filtering .exc {
    display: none;
  }
  .lamps-only .lifts,
  .filtering.lamps-only .lc {
    display: none;
  }
  .lamps-only .dots {
    display: flex;
  }
  .lamps-only .tally {
    border-top: 0;
    margin-top: 0;
  }
  .lamps-only .lc {
    font-size: 11px;
  }
  .filtering:not(.lamps-only):not(.mini) .rc::before {
    content: '·';
    margin-right: 1px;
  }
  .filtering:not(.mini):not(.open) {
    padding-top: 11px;
  }
  .hits {
    display: flex;
    flex-wrap: wrap;
    column-gap: 14px;
    align-content: flex-start;
    overflow: hidden;
    margin-top: 4px;
    padding: 2px 0 7px;
  }
  .filtering:not(.lamps-only) .hits,
  .named .hits {
    border-top: 1px solid var(--line);
    margin-top: 6px;
    padding-top: 3px;
  }
  .named .hits {
    margin-top: 0;
  }
  .hit {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: var(--row);
    font-size: 14px;
    white-space: nowrap;
  }
  .hit.off {
    color: var(--ink-3);
  }
  .hit.lab {
    color: var(--ink-3);
    font-size: 11px;
  }
  .hit.more {
    color: var(--ink-2);
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  /* Opened: the runs by grade in two columns, the only thing that scrolls. */
  .open {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .open {
    padding-top: 0;
  }
  .open .top {
    flex: none;
    padding-top: 9px;
    min-height: 44px;
  }
  .open .lifts {
    padding-bottom: 6px;
  }
  .filtering.open .lc {
    display: none;
  }
  .runs {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    margin: 0 -14px;
    padding: 0 14px 18px;
    border-top: 1px solid var(--line);
    mask-image: linear-gradient(#000 calc(100% - 28px), transparent);
  }

  /* Short phones (375 × 667): lifts fold into lamps in the header line. */
  @media (max-width: 999px) and (max-height: 760px) {
    .lifts {
      display: none;
    }
    .dots {
      display: flex;
    }
    .lc {
      font-size: 11px;
    }
    .tally {
      border-top: 0;
      margin-top: 0;
      height: calc(var(--row) + 8px);
    }
    /*
     * Opened, the head stays one slim line so the list keeps its rows; its
     * target reaches into the gap above and the list's top edge to make 44 px.
     */
    .open .top {
      min-height: 0;
      padding-bottom: 6px;
    }
    .open .ph::after {
      inset: -6px 0 -5px;
      border-radius: var(--r) var(--r) 0 0;
    }
  }
  @media (max-width: 400px) {
    .plate {
      padding-left: 13px;
      padding-right: 13px;
    }
    .lift {
      font-size: 14px;
      gap: 8px;
    }
    .tally {
      gap: 10px;
      font-size: 13px;
    }
    .t {
      gap: 5px;
    }
    .runs {
      margin: 0 -13px;
      padding: 0 13px 18px;
    }
    .lbl {
      letter-spacing: 0.1em;
    }
  }

  /* Desktop: the plates sit beside the board and never change shape. */
  .desk {
    padding: 11px 15px 0;
  }
  .desk .lifts {
    --row: 25px;
  }
  .desk .tally {
    height: 40px;
  }
  .desk .lift {
    font-size: 14px;
  }
  .desk.mini {
    padding-top: 0;
  }
  .sel {
    border-color: light-dark(oklch(0.5 0.12 62 / 0.55), oklch(0.8 0.15 72 / 0.45));
  }
  .sel .lbl {
    color: var(--amber-ink);
  }
</style>
